import { EntityFormModal } from '@/components/EntityFormModal'
import { useSeededModalField } from '@/hooks/useSeededModalField'
import { evaluateIdentifierField, IDENTIFIER_RULE_MESSAGE } from '@/lib/identifierValidation'
import type { ExtractorsFile, GlobalDefaultsFile, ProductType } from '@/types/bundle'
import { buildEmptyProductType, cloneForDuplicateProductType } from '../constants'

export interface NewOrDuplicateProductTypeModalProps {
  open: boolean
  onClose: () => void
  existingIds: string[]
  hardCaps: GlobalDefaultsFile['hard_caps']
  extractors: ExtractorsFile['extractors']
  /** Present for 'duplicate', absent for '+ New' - drives title/prefill logic,
   * same pattern as document-types' NewOrDuplicateDocumentTypeModal. */
  source?: { id: string; product: ProductType }
  onConfirm: (newId: string, initialEntry: ProductType) => void
}

// Both "+ New Product Type" and per-row "Duplicate" open this same form
// (empty vs. pre-filled) - mirrors NewOrDuplicateDocumentTypeModal's own
// pattern exactly. A product_type_id is mandatory either way since JSON
// object keys can't literally duplicate.
export function NewOrDuplicateProductTypeModal({
  open,
  onClose,
  existingIds,
  hardCaps,
  extractors,
  source,
  onConfirm,
}: NewOrDuplicateProductTypeModalProps) {
  const openKey = open ? (source?.id ?? '__new__') : null
  const { value: id, setValue: setId, reset } = useSeededModalField(openKey, () =>
    source ? `${source.id}_copy` : '',
  )

  const { trimmedId, isDuplicateId, isInvalidShape, isValid } = evaluateIdentifierField(id, existingIds)

  function handleClose() {
    reset()
    onClose()
  }

  function handleConfirm() {
    if (!isValid) return
    const initialEntry = source
      ? cloneForDuplicateProductType(source.product)
      : buildEmptyProductType(hardCaps, extractors)
    onConfirm(trimmedId, initialEntry)
    handleClose()
  }

  return (
    <EntityFormModal
      open={open}
      entityLabel="Product Type"
      isDuplicate={Boolean(source)}
      isValid={isValid}
      onClose={handleClose}
      onConfirm={handleConfirm}
    >
      <div className="flex flex-col gap-1">
        <label className="text-meta font-medium text-text-secondary" htmlFor="new-product-type-id">
          Product type ID
        </label>
        <input
          id="new-product-type-id"
          type="text"
          autoFocus
          value={id}
          onChange={(event) => setId(event.target.value)}
          placeholder="e.g. title_deed_extract"
          className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none placeholder:text-text-disabled"
        />
        {isDuplicateId && <span className="text-meta text-danger">A product type with this ID already exists</span>}
        {isInvalidShape && <span className="text-meta text-danger">{IDENTIFIER_RULE_MESSAGE}</span>}
      </div>
    </EntityFormModal>
  )
}
